import type { Database, Tables } from '@/types/database.types'
import type { Subscription } from '@supabase/supabase-js'
import mapCompanyMembershipAttributes from '@/utils/useMemembershipMapper'

type Profile = Tables<'profiles'> & { email?: string }
type Company = Tables<'companies'> | null
type InitialData = {
  profile?: Profile
  company?: Company
} | null

interface CurrentUserState {
  supabaseAuthSubscription: Subscription | undefined
  profile: Profile | null
  company: Company | null
}

export const useUserStoreSsr = () => {
  const state = useState<CurrentUserState>('user-store', () =>
    reactive({
      profile: null,
      company: null,
      supabaseAuthSubscription: undefined,
    })
  )

  const { supabaseAuthSubscription, profile, company } = toRefs(state.value)

  // MARK: Getters
  const isSupplier = computed(() => {
    if (!company.value) return false
    return !!company.value.is_supplier
  })

  const membershipBenefits = computed(() => {
    if (!company.value) return null
    return mapCompanyMembershipAttributes(company.value)
  })

  //  MARK: Actions
  async function initialiseState(initialData?: InitialData): Promise<void> {
    if (!initialData) {
      const data = await fetchProfileAndCompany()

      profile.value = data?.profile ?? null
      company.value = data?.company ?? null
    } else {
      profile.value = initialData.profile ?? null
      company.value = initialData.company ?? null
    }
  }

  async function reloadProfile() {
    await initialiseState()
  }

  // MARK: Auth subscription handler
  if (import.meta.client) {
    const supabase = useSupabaseClient<Database>()
    const { data } = supabase.auth.onAuthStateChange((event) => {
      switch (event) {
        case 'INITIAL_SESSION':
        case 'SIGNED_IN':
        case 'USER_UPDATED':
          setTimeout(async () => {
            await initialiseState()
          }, 0)
          break
        case 'SIGNED_OUT':
          profile.value = null
          company.value = null
          supabaseAuthSubscription.value = undefined
          break
        default:
          break
      }
    })

    supabaseAuthSubscription.value = data.subscription
  }

  return {
    profile: readonly(profile),
    company: readonly(company),
    isSupplier,
    membershipBenefits,
    reloadProfile,
    initialiseState,
    supabaseAuthSubscription: readonly(supabaseAuthSubscription),
  }
}

export async function fetchProfileAndCompany(): Promise<{
  profile: Tables<'profiles'> & { email?: string }
  company: Tables<'companies'> | null
} | null> {
  try {
    const data = await $fetch('/api/profile')
    if (data) {
      const { company, ...profile } = data
      return { company, profile }
    }
    return null
  } catch (error) {
    console.error(error)
    return null
  }
}
